/** Marca de una semana devuelta por getPerfilAsistencia (mayorap2). */
export interface AsistenciaMarca {
  domingo: boolean;
  celula: boolean;
}

/**
 * asist0..asist4. En la tabla de mayorap2, asist0 va en la columna más vieja
 * (consul5) y asist4 en la más reciente (consul1).
 */
export interface PerfilAsistenciaResponse {
  asist0: AsistenciaMarca | null;
  asist1: AsistenciaMarca | null;
  asist2: AsistenciaMarca | null;
  asist3: AsistenciaMarca | null;
  asist4: AsistenciaMarca | null;
}

export interface ConsulFechas {
  /** consul1..consul5: índice 0 = semana más reciente. Formato DD-MM-YYYY. */
  display: [string, string, string, string, string];
  /** Misma posición, formato YYYY-MM-DD para el path de la API. */
  iso: [string, string, string, string, string];
}

export interface SemanaAsistencia {
  label: string;
  iso: string;
  marca: AsistenciaMarca | null;
}

const OFFSETS = [0, -7, -14, -21, -28] as const;

function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function addDays(date: Date, days: number): Date {
  const next = startOfDay(date);
  next.setDate(next.getDate() + days);
  return next;
}

function pad2(value: number): string {
  return String(value).padStart(2, '0');
}

function formatDisplay(date: Date): string {
  return `${pad2(date.getDate())}-${pad2(date.getMonth() + 1)}-${date.getFullYear()}`;
}

function formatIso(date: Date): string {
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
}

/**
 * Domingo vigente guardado por la iglesia (`YYYY-MM-DD`), sin corrimiento UTC.
 * Es la fecha contra la que se informa la lista.
 */
export function fechaDomLocal(ymd: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd.trim());
  if (!match) return null;
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  return Number.isNaN(date.getTime()) ? null : date;
}

/** Domingo de la semana de `hoy` (o el mismo día si ya es domingo). */
export function ultimoDomingo(hoy: Date = new Date()): Date {
  const day = startOfDay(hoy);
  day.setDate(day.getDate() - day.getDay());
  return day;
}

/**
 * consul1 = fecha base, consul2 = -7 días, … consul5 = -28 días.
 * Igual que generarFechas() de mayorap2.
 */
export function buildConsulFechas(base: Date): ConsulFechas {
  const dates = OFFSETS.map((offset) => addDays(base, offset));
  return {
    display: dates.map(formatDisplay) as ConsulFechas['display'],
    iso: dates.map(formatIso) as ConsulFechas['iso'],
  };
}

/** Columnas de izquierda a derecha: más vieja → más reciente (consul5 … consul1). */
export function mapSemanasAsistencia(
  base: Date,
  data: PerfilAsistenciaResponse | null,
): SemanaAsistencia[] {
  const fechas = buildConsulFechas(base);
  const marcas: (AsistenciaMarca | null)[] = [
    data?.asist0 ?? null,
    data?.asist1 ?? null,
    data?.asist2 ?? null,
    data?.asist3 ?? null,
    data?.asist4 ?? null,
  ];

  return [4, 3, 2, 1, 0].map((consulIndex, column) => ({
    label: fechas.display[consulIndex],
    iso: fechas.iso[consulIndex],
    marca: marcas[column],
  }));
}
